import type { DocumentRef } from '../../lib/documentReader'

export type BookObjectKind = 'entity' | 'instrument' | 'position' | 'trade'
export type BookValueStateName = 'missing' | 'null' | 'value'
export type BookFieldChange = 'unchanged' | 'changed' | 'new' | 'unavailable'
export type BookProposalOrigin = 'current' | 'engine_proposal' | 'officer_edit'

export interface BookValueState {
  state: BookValueStateName
  value: unknown
}

export interface BookOperationShape {
  required: boolean
  nullable: boolean
  value_schema: Record<string, unknown>
}

export interface BookFieldDefinition {
  key: string
  label: string
  order: number
  value_kind: string
  format: string
  currency_key?: string | null
  basis_key?: string | null
  origin?: 'schema' | 'relationship' | 'derived' | 'status'
  editable: boolean
  create?: BookOperationShape | null
  correction?: BookOperationShape | null
  projection?: string | null
  identity_projection?: string | null
}

export interface BookEditorSchema {
  type?: string
  format?: string
  enum?: readonly unknown[]
  properties?: Record<string, BookEditorSchema>
  required?: readonly string[]
  items?: BookEditorSchema
  anyOf?: readonly BookEditorSchema[]
  oneOf?: readonly BookEditorSchema[]
}

export interface BookEditorOption {
  value: string
  label: string
}

export interface BookEditorModel {
  kind: 'text' | 'date' | 'number' | 'enum' | 'boolean' | 'object' | 'array'
  schema: BookEditorSchema
  options?: readonly BookEditorOption[]
}

export interface BookSourceAssertion {
  document: DocumentRef
  artifact_type: 'document_object_link' | 'evidence_item' | 'commitment_review_assessment'
  artifact_id: string
  annotation_id?: string | null
  source_key: string
  financial_date?: string | null
  value: BookValueState
  page?: number | null
  quote?: string | null
  revision: string
}

export interface BookFieldReview {
  definition: BookFieldDefinition
  current: BookValueState
  current_display: BookValueState
  /** The selected document's assertion. It is the document-state column only. */
  source_assertions: readonly BookSourceAssertion[]
  /** Additive API field for a known confirming document on the current Record. */
  current_source_assertions?: readonly BookSourceAssertion[]
  engine_proposed: BookValueState
  officer_edited: BookValueState
  proposed: BookValueState
  proposed_display: BookValueState
  proposal_key?: string | null
  intent: 'keep' | 'set'
  change: BookFieldChange
  proposed_origin: BookProposalOrigin
  required: boolean
  mandatory_empty: boolean
  show_initially: boolean
  validation_issue?: string | null
}

export interface BookCapitalLeg {
  position_id: string
  position_role: 'commitment' | 'undrawn'
  checkpoint_value: string
  checkpoint_date: string
  checkpoint_document_id: string | null
  current_value: string
  current_date: string
  movements: readonly {
    trade_id: string
    financial_date: string
    units_delta: string
    document_id: string | null
  }[]
  effect_delta: string
  resulting_value: string
  resulting_date: string
  currency: string
}
export interface BookDeferredIssue {
  missing_references?: readonly {
    title: string
    kind: 'document' | 'information'
    document: DocumentRef
  }[]
  issue_key: string
  location: string
  area: 'capital' | 'cash'
  issue: string
  review_reason: string
  applicability: 'future_event' | 'outside_action'
  reconsider_on: readonly (
    | 'new_source'
    | 'changed_action'
    | 'admission'
    | 'funding'
    | 'distribution'
    | 'default'
    | 'transfer'
    | 'expiry'
  )[]
  affected_fields: readonly ('amount' | 'effective_date' | 'identity' | 'rights')[]
  current_fact_impact: false
  documents: readonly DocumentRef[]
  review_documents: readonly DocumentRef[]
}

export interface BookNoncoreFlag {
  field: string
  reason: string
  effect: string
  affected_fields: readonly string[]
  missing_references: readonly {
    title: string
    kind: 'document' | 'information'
    document: DocumentRef
  }[]
  documents: readonly DocumentRef[]
  period_version_key: string | null
  current_fact_impact: false
  financial_authority: false
}

export interface BookCapitalCalculation {
  noncore_flags?: readonly BookNoncoreFlag[]
  deferred_issues?: readonly BookDeferredIssue[]
  missing_requirements?: readonly 'governing_terms'[]
  notice_content_gaps?: readonly {
    kind: 'use_of_proceeds'
    reason: string
    current_fact_impact: false
    documents: readonly DocumentRef[]
  }[]
  financial_date: string
  currency: string
  before: { committed: string; drawn: string; undrawn: string }
  after: { committed: string; drawn: string; undrawn: string }
  legs: readonly BookCapitalLeg[]
}

export interface BookObjectReview {
  /** Seller rule A: the recorded balance post-dates this sale (read-only note). */
  seller_pre_sale_review?: {
    kind: 'seller_pre_sale_holding_undocumented'
    position_id: string
    recorded_units: string | number
    recorded_as_of: string
    sold_units: string | number
    sale_date: string
    note: string
    current_fact_impact: false
  }
  nominal_citation_review?: {
    state: 'unverified'
    amount: string | null
    quote: string | null
    cited_page: number | null
    document: DocumentRef
  } | null
  final_terms_review?: {
    trade_id: string
    draft_document_id: string
    final_document_id: string
    /** Absent = draft_final. explicit_correction: a recorded (non-draft) trade
     *  whose source the officer superseded; only its amount is corrected.
     *  capital_final: the signed agreement of a recorded provisional commitment;
     *  its amount and date replace the provisional ones on the same trade. */
    kind?: 'draft_final' | 'explicit_correction' | 'capital_final'
  } | null
  draft_date_review?: {
    date_origin: 'source_proposed_date' | 'document_receipt' | null
    effective_date: string | null
  } | null
  role?: 'primary' | 'effect'
  capital_calculation?: BookCapitalCalculation | null
  provisional_capital_review?: {
    purpose: 'provisional_initial_commitment'
    financial_date: string
    date_origin: 'source_proposed_date' | 'document_receipt'
    currency: string
    legs: readonly {
      position_id: string
      position_role: 'commitment' | 'undrawn'
      current_value: string | null
      current_date: string | null
      effect_delta: string
    }[]
  } | null
  capital_final_review?: {
    deferred_issues?: readonly BookDeferredIssue[]
    noncore_flags?: readonly BookNoncoreFlag[]
    purpose: 'provisional_commitment_final'
    trade_id: string
    draft_document_id: string
    final_document_id: string
    currency: string
    provisional_date: string
    provisional_date_origin: 'source_proposed_date' | 'document_receipt'
    provisional_amount: string
    final_date: string
    final_amount: string
    legs: readonly {
      position_id: string
      position_role: 'commitment' | 'undrawn'
      current_value: string | null
      current_date: string | null
      provisional_effect: string
      final_effect: string
      resulting_value: string | null
    }[]
  } | null
  initial_capital_review?: {
    noncore_flags?: readonly BookNoncoreFlag[]
    deferred_issues?: readonly BookDeferredIssue[]
    purpose: 'initial_commitment_effects'
    financial_date: string
    currency: string
    legs: readonly {
      position_id: string
      position_role: 'commitment' | 'undrawn'
      current_value: string | null
      current_date: string | null
      effect_delta: string
    }[]
  } | null
  opening_position_review?: {
    purpose: 'opening_checkpoint'
    financial_date: string
    currency: string
    position_role: 'commitment' | 'undrawn'
    signed_commitment: string
    opening_value: string
    derived: boolean
  } | null
  standing_review?: {
    noncore_flags?: readonly BookNoncoreFlag[]
    purpose: 'reported_balance'
    reporting_qualifications?: readonly {
      kind:
        | 'unaudited_or_subject_to_adjustment'
        | 'future_rights_not_established'
        | 'valuation_basis_note'
        | 'generic_scope_label'
      reason: string
      current_fact_impact: false
      documents: readonly DocumentRef[]
    }[]
    assessment_id: string
    document_id: string
    financial_date: string
    position_role: 'commitment' | 'undrawn'
    missing_requirements: readonly 'governing_terms'[]
  } | null
  proposal_id: string
  target_type: BookObjectKind
  target_id: string
  title?: string | null
  identity: Record<string, string | null>
  catalogue_revision: string
  current_status: 'confirmed' | 'not_recorded'
  proposal_status: string
  application_status?: 'not_assessed' | string
  prospective_effect_status?: 'not_calculated' | string
  fields: readonly BookFieldReview[]
}

export interface BookObservedHead {
  field: string
  revision: string
  record_event_id?: string | null
  checkpoint_id?: string | null
}

export interface BookRevisionBinding {
  kind: string
  id: string
  revision: string
}

export interface BookTargetBinding {
  target_type: BookObjectKind
  target_id: string
  record_state?: string | null
  value_revision: string
  fund_ids?: readonly string[]
  membership_revision?: string | null
  heads?: readonly BookObservedHead[]
}

export interface BookReviewBinding {
  purpose: 'read_snapshot_only'
  actor_id: string
  depositary_id: string
  scope: Record<string, string | null>
  group: BookRevisionBinding
  proposals: readonly BookRevisionBinding[]
  sources: readonly BookRevisionBinding[]
  targets: readonly BookTargetBinding[]
  actor_revision: string
  value: string
}

export interface BookReviewAction {
  id: string
  label: string
  proposal_id?: string | null
  object_id?: string | null
  /** `target_id` is accepted while the API lane settles naming. */
  target_id?: string | null
  kind?: 'book' | 'book_all' | 'edit' | 'other'
  disabled?: boolean
  disabled_reason?: string | null
  pending?: boolean
  requires_reason?: boolean
  dependency_ids?: readonly string[]
}

export interface BookReview {
  objects: readonly BookObjectReview[]
  documents: readonly DocumentRef[]
  binding: BookReviewBinding
  /** Additive sentence supplied by the deck read model when available. */
  sentence?: string | null
  /** Additive UI action projection; the base read DTO remains read-only. */
  actions?: readonly BookReviewAction[]
}

export interface BookSourceLink {
  annotationId?: string | null
  document: DocumentRef
  quote?: string | null
  page?: number | null
}

export interface BookDocumentClaim {
  key: string
  state: BookValueState
  text: string
  financialDate: string | null
  dateText: string | null
  source: BookSourceLink
}

export interface BookFieldView {
  key: string
  label: string
  definition: BookFieldDefinition
  current: BookValueState
  currentText: string
  currentSources: readonly BookSourceLink[]
  documentState: BookValueState
  documentText: string
  documentSources: readonly BookSourceLink[]
  documentClaims: readonly BookDocumentClaim[]
  proposed: BookValueState
  proposedText: string
  changed: boolean
  showInitially: boolean
  editable: boolean
  proposalKey: string | null
  required: boolean
  mandatoryEmpty: boolean
  validationIssue: string | null
}

export interface BookObjectView extends Omit<BookObjectReview, 'fields'> {
  capitalLeg?: BookCapitalLeg
  fields: readonly BookFieldView[]
  actions: readonly BookReviewAction[]
  isNew: boolean
}

export interface BookReviewView {
  sentence: string | null
  documents: readonly DocumentRef[]
  objects: readonly BookObjectView[]
  actions: readonly BookReviewAction[]
}

const missingState = (): BookValueState => ({ state: 'missing', value: null })

function labelForKey(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/^./, (char) => char.toUpperCase())
}

function resolvedSchema(schema: BookEditorSchema): BookEditorSchema {
  const alternatives = schema.anyOf ?? schema.oneOf
  if (!alternatives?.length) return schema
  return alternatives.find((item) => item.type !== 'null') ?? alternatives[0]
}

function schemaForField(field: BookFieldView): BookEditorSchema {
  return resolvedSchema(
    (field.definition.correction?.value_schema ??
      field.definition.create?.value_schema ??
      {}) as BookEditorSchema
  )
}

function enumOptions(schema: BookEditorSchema): readonly BookEditorOption[] {
  return (schema.enum ?? [])
    .filter((value) => value !== null && value !== undefined)
    .map((value) => ({ value: String(value), label: labelForKey(String(value)) }))
}

export function bookFieldEditor(field: BookFieldView): BookEditorModel {
  const schema = schemaForField(field)
  const options = enumOptions(schema)
  if (options.length) return { kind: 'enum', schema, options }
  if (schema.type === 'boolean') return { kind: 'boolean', schema }
  if (schema.type === 'object' || schema.properties) return { kind: 'object', schema }
  if (schema.type === 'array' || schema.items) return { kind: 'array', schema }
  if (schema.format === 'date' || field.definition.format === 'date')
    return { kind: 'date', schema }
  if (
    ['number', 'integer'].includes(schema.type ?? '') ||
    ['number', 'quantity', 'money'].includes(field.definition.format)
  )
    return { kind: 'number', schema }
  return { kind: 'text', schema }
}

export function parseBookEditorValue(schema: BookEditorSchema, raw: string): unknown {
  const resolved = resolvedSchema(schema)
  if (resolved.type === 'boolean') return raw === '' ? null : raw === 'true'
  // Keep decimals as strings: Number() would silently lose precision.
  if (['number', 'integer'].includes(resolved.type ?? '')) return raw
  return raw
}

function readableValue(value: unknown, depth = 0): string {
  if (value === null || value === undefined) return 'Not stated'
  if (depth > 3) return '…'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'string' || typeof value === 'number') return String(value)
  if (Array.isArray(value)) {
    if (!value.length) return 'No entries'
    return value.map((item, index) => `${index + 1}. ${readableValue(item, depth + 1)}`).join(' · ')
  }
  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .map(([key, item]) => `${labelForKey(key)}: ${readableValue(item, depth + 1)}`)
      .join(' · ')
  }
  return String(value)
}

function valueText(
  state: BookValueState | undefined,
  missingLabel = 'Not available',
  format = 'text'
): string {
  if (!state || state.state === 'missing') return missingLabel
  if (state.state === 'null' || state.value === null || state.value === undefined)
    return 'Not stated'
  if (typeof state.value === 'boolean') return state.value ? 'Yes' : 'No'
  if (typeof state.value === 'number')
    return new Intl.NumberFormat('en-GB', { maximumFractionDigits: 6 }).format(state.value)
  if (typeof state.value === 'string') {
    if (format === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(state.value)) {
      const [year, month, day] = state.value.split('-')
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ]
      const monthName = months[Number(month) - 1]
      if (monthName) return `${Number(day)} ${monthName} ${year}`
    }
    if (['number', 'quantity', 'money'].includes(format) && /^-?\d+(?:\.\d+)?$/.test(state.value)) {
      const [whole, fraction] = state.value.split('.')
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      return fraction === undefined ? grouped : `${grouped}.${fraction}`
    }
    return state.value
  }
  if (Array.isArray(state.value)) return readableValue(state.value)
  if (typeof state.value === 'object') return readableValue(state.value)
  return String(state.value)
}

function sourceLink(assertion: BookSourceAssertion): BookSourceLink {
  return {
    annotationId: assertion.annotation_id,
    document: {
      ...assertion.document,
      page: assertion.page ?? assertion.document.page,
      snippet: assertion.quote ?? assertion.document.snippet,
    },
    quote: assertion.quote,
    page: assertion.page,
  }
}

function sourceAssertions(field: BookFieldReview, current: boolean): readonly BookSourceLink[] {
  const assertions = current
    ? (field.current_source_assertions ?? [])
    : (field.source_assertions ?? [])
  return assertions.map(sourceLink)
}

function displayState(display: BookValueState, raw: BookValueState): BookValueState {
  return display.state === 'missing' ? raw : display
}

function documentClaims(field: BookFieldReview): readonly BookDocumentClaim[] {
  const assertions = field.source_assertions ?? []
  const showDates =
    assertions.some((assertion) => Boolean(assertion.financial_date)) ||
    (assertions.length > 1 && ['number', 'quantity', 'money'].includes(field.definition.format))
  const occurrences = new Map<string, number>()
  return assertions.map((assertion) => {
    const source = sourceLink(assertion)
    const identity = JSON.stringify([
      assertion.artifact_type,
      assertion.artifact_id,
      assertion.source_key,
      assertion.revision,
      assertion.annotation_id ?? null,
      assertion.document.document_id,
      assertion.financial_date ?? null,
      assertion.value,
      source.document.page ?? null,
      source.document.snippet ?? null,
    ])
    const occurrence = occurrences.get(identity) ?? 0
    occurrences.set(identity, occurrence + 1)
    return {
      key: `${identity}:${occurrence}`,
      state: assertion.value,
      text: valueText(assertion.value, 'Not stated in document', field.definition.format),
      financialDate: assertion.financial_date ?? null,
      dateText:
        field.definition.format === 'date' && assertion.value.value === assertion.financial_date
          ? null
          : assertion.financial_date
            ? valueText({ state: 'value', value: assertion.financial_date }, '', 'date')
            : showDates
              ? 'Date not stated'
              : null,
      source,
    }
  })
}

function fieldView(field: BookFieldReview): BookFieldView {
  const current = displayState(field.current_display, field.current)
  const proposed = displayState(field.proposed_display, field.proposed)
  const claims = documentClaims(field)
  // A history/conflict is not one scalar assertion. Retain every exact claim;
  // legacy scalar consumers receive no arbitrary first-value winner.
  const documentState = claims.length === 1 ? claims[0].state : missingState()
  return {
    key: field.definition.key,
    label: field.definition.label,
    definition: field.definition,
    current,
    currentText: valueText(current, 'Not recorded', field.definition.format),
    currentSources: sourceAssertions(field, true),
    documentState,
    documentText: claims.length
      ? claims.map((claim) => [claim.text, claim.dateText].filter(Boolean).join(' · ')).join('; ')
      : 'Not stated in document',
    documentSources: claims.map((claim) => claim.source),
    documentClaims: claims,
    proposed,
    proposedText: valueText(proposed, 'Not proposed', field.definition.format),
    changed: field.change === 'changed' || field.change === 'new',
    showInitially:
      field.show_initially ||
      field.change === 'changed' ||
      field.change === 'new' ||
      field.mandatory_empty,
    editable:
      field.definition.editable &&
      typeof field.proposal_key === 'string' &&
      field.proposal_key.length > 0,
    proposalKey: field.proposal_key ?? null,
    required: field.required,
    mandatoryEmpty: field.mandatory_empty,
    validationIssue: field.validation_issue ?? null,
  }
}

function actionObjectId(action: BookReviewAction): string | null {
  return action.object_id ?? action.target_id ?? null
}

function dedupeDocuments(documents: readonly DocumentRef[]): readonly DocumentRef[] {
  const byId = new Map<string, DocumentRef>()
  for (const document of documents) {
    if (!document.document_id) continue
    const previous = byId.get(document.document_id)
    if (!previous || (!previous.display_title && document.display_title))
      byId.set(document.document_id, document)
  }
  return [...byId.values()]
}

export function formatBookValue(state: BookValueState | undefined, missingLabel = 'Not available') {
  return valueText(state, missingLabel)
}

export function toBookReviewView(review: BookReview): BookReviewView {
  const actions = [...(review.actions ?? [])]
  const objects = review.objects.map((object) => {
    const objectActions =
      object.role === 'effect'
        ? []
        : actions.filter((action) => {
            const objectId = actionObjectId(action)
            return objectId === object.target_id || action.proposal_id === object.proposal_id
          })
    return {
      ...object,
      fields: object.fields.map((field) => {
        const value = fieldView(field)
        return object.role === 'effect' ? { ...value, editable: false, proposalKey: null } : value
      }),
      capitalLeg:
        object.role === 'effect'
          ? review.objects
              .find((row) => row.proposal_id === object.proposal_id && row.role !== 'effect')
              ?.capital_calculation?.legs.find((leg) => leg.position_id === object.target_id)
          : undefined,
      actions: objectActions,
      isNew: object.current_status === 'not_recorded',
    }
  })
  return {
    sentence: review.sentence ?? null,
    documents: dedupeDocuments(review.documents),
    objects,
    actions,
  }
}

export function visibleBookFields(
  fields: readonly BookFieldView[],
  expanded: boolean
): readonly BookFieldView[] {
  return expanded ? fields : fields.filter((field) => field.showInitially)
}

/** Display the paired balances together. Drawn is derived, never another position. */
export function pairedCapitalRows<K extends string>(
  legs: readonly ({
    position_id: string
    position_role: 'commitment' | 'undrawn'
    current_date: string | null
  } & Record<K, string | null>)[],
  columns: readonly K[]
): {
  role: 'commitment' | 'drawn' | 'undrawn'
  label: string
  values: Record<K, string | null>
  currentDate: string | null
}[] {
  const one = (role: 'commitment' | 'undrawn') => {
    const matches = legs.filter((leg) => leg.position_role === role)
    return matches.length === 1 ? matches[0] : undefined
  }
  const commitment = one('commitment')
  const undrawn = one('undrawn')
  const distinctPair = commitment && undrawn && commitment.position_id !== undrawn.position_id
  return (['commitment', 'drawn', 'undrawn'] as const).map((role) => {
    const leg = role === 'commitment' ? commitment : role === 'undrawn' ? undrawn : undefined
    return {
      role,
      label: role === 'commitment' ? 'Commitment' : role === 'drawn' ? 'Drawn' : 'Undrawn',
      values: Object.fromEntries(
        columns.map((column) => [
          column,
          role === 'drawn'
            ? distinctPair
              ? addPairedDecimal(commitment[column], undrawn[column])
              : null
            : (leg?.[column] ?? null),
        ])
      ) as Record<K, string | null>,
      currentDate:
        role === 'drawn'
          ? distinctPair && commitment.current_date === undrawn.current_date
            ? commitment.current_date
            : null
          : (leg?.current_date ?? null),
    }
  })
}

function addPairedDecimal(left: string | null, right: string | null): string | null {
  // Match the canonical decimal strings used by the existing officer formatter.
  // Never pass money through Number, or invent zero for a missing balance.
  if (
    left === null ||
    right === null ||
    !/^-?\d+(?:\.\d+)?$/.test(left) ||
    !/^-?\d+(?:\.\d+)?$/.test(right)
  )
    return null
  const scale = Math.max(left.split('.')[1]?.length ?? 0, right.split('.')[1]?.length ?? 0)
  const integer = (value: string) => {
    const negative = value.startsWith('-')
    const [whole, fraction = ''] = (negative ? value.slice(1) : value).split('.')
    return BigInt(whole + fraction.padEnd(scale, '0')) * (negative ? -1n : 1n)
  }
  const sum = integer(left) + integer(right)
  if (sum === 0n) return '0'
  const digits = (sum < 0n ? -sum : sum).toString().padStart(scale + 1, '0')
  const fraction = scale ? digits.slice(-scale).replace(/0+$/, '') : ''
  return `${sum < 0n ? '-' : ''}${scale ? digits.slice(0, -scale) : digits}${fraction ? '.' + fraction : ''}`
}

/** Display capacity without changing the stored signed amount. */
export function pairedCapitalDisplayValue(
  role: 'commitment' | 'drawn' | 'undrawn',
  value: string | null
): string | null {
  if (role !== 'undrawn' || value === null) return value
  if (!/^-?\d+(?:\.\d+)?$/.test(value)) return null
  if (/^-?0+(?:\.0+)?$/.test(value)) return '0'
  return value.startsWith('-') ? value.slice(1) : `-${value}`
}
